import React, { useState } from "react";
import { cloneElement, isValidElement } from "react";
import { useSubscriptionHelper } from "./useSubscriptionHelper";
import UpgradePlanDialog from "@/components/upgradePlanDialog";
import { Lock } from "lucide-react";
import { useCountInvestors } from "../shares/useCountInvestors";
import { useSubscriptionPlans } from "@/hooks/subscriptions/useSubscriptionPlans";
const useRestrictAccess = (companyId) => {
  const [countInvestorsEnabled, setCountInvestorsEnabled] = useState(false);

  const { data: countInvestorsQuery, isLoading: isLoadingInvestorsCounter } =
    useCountInvestors({ enabled: countInvestorsEnabled, retry: false });
  const currentNumberOfInvestors = countInvestorsQuery?.data;

  const { data: subscriptionPlansQuery } = useSubscriptionPlans();
  const subscriptionPlans = subscriptionPlansQuery?.data;

  const {
    data: subscriptionHelperQuery,
    isLoading: isLoadingSubscriptionHelper,
  } = useSubscriptionHelper(companyId);
  const subscriptionHelper = subscriptionHelperQuery?.data;
  const plansByFeature = subscriptionHelper?.plansByFeature;

  const [restrictDialogElement, setRestrictDialogElement] = useState(null);

  const hasAccess = (feature) => {
    if (!plansByFeature || !subscriptionHelper) return false;

    const userPlan = subscriptionHelper.account;
    return plansByFeature[feature]?.[userPlan] ?? false;
  };

  /**
   *
   * @param {JSX.Element | Function} subject
   * @param {object} options
   * @param {string} options.requiredFeature
   * @param {function} [options.onModalOpen]
   * @returns {JSX.Element}
   */
  const restrict = (subject, options) => {
    if (!options?.requiredFeature) {
      // No feature requirement, render as is
      return typeof subject === "function"
        ? subject({
            hasAccess: true,
            resolveRestrictionOnClick: (fn) => fn,
            renderRestrictionIcon: (el) => el,
          })
        : subject;
    }

    // Don't render until we can determine if the user has access
    if (isLoadingSubscriptionHelper || !plansByFeature) {
      return null;
    }

    if (
      options?.requiredFeature === "share-register-basic" &&
      !isLoadingInvestorsCounter &&
      !countInvestorsEnabled
    ) {
      setCountInvestorsEnabled(true);
    }

    let userHasAccess = true;
    let requiredPlan = "free";

    if (options?.requiredFeature) {
      userHasAccess = hasAccess(options.requiredFeature);

      requiredPlan =
        (plansByFeature[options.requiredFeature].free && "free") ||
        (plansByFeature[options.requiredFeature].lite && "lite") ||
        (plansByFeature[options.requiredFeature].standard && "standard") ||
        (plansByFeature[options.requiredFeature].premium && "premium");
    }

    if (
      options?.requiredFeature === "share-register-basic" &&
      !!currentNumberOfInvestors &&
      !!subscriptionPlans
    ) {
      userHasAccess =
        currentNumberOfInvestors?.totalNumOfInvestors <
        subscriptionHelper?.maxNumberOfShareholders;

      requiredPlan =
        subscriptionPlans?.account?.month?.find?.(
          (p) =>
            Number(p.metadata?.maxNumberOfShareholders) >
            currentNumberOfInvestors?.totalNumOfInvestors,
        )?.metadata?.plan ?? "premium";
    }

    const onClickTriggerModal = async (evt) => {
      evt.preventDefault(); // Used for components that may have additional onClick handlers attached
      evt.stopPropagation();

      options?.onModalOpen?.();

      setRestrictDialogElement(
        <UpgradePlanDialog
          feature={options.requiredFeature}
          requiredPlan={requiredPlan}
          onCancel={() => setRestrictDialogElement(null)}
        />,
      );
    };

    if (typeof subject === "function") {
      return subject({
        hasAccess: userHasAccess,
        resolveRestrictionOnClick: (originalOnClickFn) =>
          userHasAccess ? originalOnClickFn : onClickTriggerModal,
        renderRestrictionIcon: (originalElement, size) =>
          userHasAccess ? (
            originalElement
          ) : (
            <Lock size={size} className="text-orange-300" />
          ),
        requiredPlan,
      });
    }

    if (userHasAccess) return subject;
    if (isValidElement(subject)) {
      // clone the element and add the parameters
      return cloneElement(
        subject,
        {
          onClick: onClickTriggerModal,
        },
        <div className="flex items-center gap-2">
          {subject.props.children}
          <Lock size={16} className="text-orange-300" />
        </div>,
      );
    }
  };

  return { restrict, hasAccess, restrictDialogElement };
};

export { useRestrictAccess };
